import * as React from "react";
import { invariant } from "@plans/appkit";
import type { ToastManager } from "@plans/appkit";
import type { ToastVariant } from "~/ui/types";

export function useToast() {
	const toastManager = React.useContext(ToastContext);
	invariant(toastManager, "Use within a ToastContext");

	const state = React.useSyncExternalStore(
		(onStoreChange) => toastManager.subscribe("change", onStoreChange),
		() => toastManager.state,
		() => toastManager.state,
	);

	const toast = React.useCallback(
		(...args: Parameters<typeof toastManager.toast>) =>
			toastManager.toast(...args),
		[toastManager],
	);

	const clearToast = React.useCallback(
		(...args: Parameters<typeof toastManager.clearToast>) =>
			toastManager.clearToast(...args),
		[toastManager],
	);

	const setToastLifetime = React.useCallback(
		(...args: Parameters<typeof toastManager.setToastLifetime>) =>
			toastManager.setToastLifetime(...args),
		[toastManager],
	);

	return {
		toast,
		clearToast,
		setToastLifetime,
		state,
	};
}

export const ToastContext = React.createContext<
	ToastManager<ToastVariant, React.ReactNode> | undefined
>(undefined);
